/** @format */

import { ActivatedRoute, Router } from '@angular/router';
import {
  Component,
  NgZone,
  OnChanges,
  OnInit,
  SimpleChanges,
  effect,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  HomefieldIconLibraryModule,
  HomefieldIconsLibraryService,
  homefieldIconChevronLeftOutline,
  homefieldIconChevronRightOutline,
} from '@dsg-tech/homefield-angular-icons';

import { map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'shared-favorites-components-auth-non-store-pagination',
  templateUrl: './non-store-pagination.component.html',
  styleUrl: './non-store-pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [HomefieldIconLibraryModule],
})
export class NonStorePaginationComponent implements OnInit, OnChanges {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly ngZone = inject(NgZone);
  private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

  totalPages = 0;
  displayPages: number[] = [];
  selectedIndex = 0;
  currentPageNumber = 0;
  showRightEllipsis = false;
  showLeftEllipsis = false;
  private readonly pageNumber = toSignal(
    this.activatedRoute.queryParamMap.pipe(
      map((queryParamMap) => {
        const pageNumber = Number(queryParamMap.get('page'));
        return Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1;
      }),
    ),
    { initialValue: 1 },
  );

  public itemLength = input(0);

  public itemsPerPage = input(24);

  public activePage = input(1);

  public activePageChange = output<number>();

  public pages: number[] = [];
  public firstPageIsNotActive = false;

  constructor() {
    this.homefieldIconsLibraryService.registerIcons([
      homefieldIconChevronLeftOutline,
      homefieldIconChevronRightOutline,
    ]);

    effect(() => {
      const pageNumber = this.resolveRoutePageNumber();
      if (!this.pages.length || pageNumber === this.currentPageNumber) {
        return;
      }

      this.generatePagination(pageNumber, this.pages.indexOf(pageNumber));
    });
  }

  ngOnInit(): void {
    this.getNumberOfPages();
    const pageNumber = this.resolveRoutePageNumber();
    this.generatePagination(pageNumber, this.pages.indexOf(pageNumber));
  }

  ngOnChanges(changes: SimpleChanges) {
    // reset pages to
    if (changes.itemLength) {
      this.getNumberOfPages();
      const pageNumber = this.resolveRoutePageNumber();
      this.generatePagination(pageNumber, this.pages.indexOf(pageNumber));
    }
  }

  private resolveRoutePageNumber(): number {
    const routePageNumber = this.pageNumber();

    if (!Number.isFinite(routePageNumber) || !this.pages.length) {
      return this.activePage();
    }

    return Math.min(Math.max(routePageNumber, 1), this.pages.length);
  }

  updatePage(pageNumber: number) {
    // add queryparam with out reloading the view
    this.ngZone.run(() => {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { page: `${pageNumber}` },
        queryParamsHandling: 'merge',
      });
    });
    this.activePageChange.emit(pageNumber);
  }

  public getNumberOfPages() {
    const numberOfPages = Math.ceil(this.itemLength() / this.itemsPerPage());
    this.pages = Array.from({ length: numberOfPages }, (_, i) => i + 1);
  }

  pageNumberCountClickEvent(pageNumber: number, index?: number): void {
    this.generatePagination(pageNumber, index);
    this.updatePage(pageNumber);
  }

  generatePagination(pageNumber: number, index?: number) {
    this.selectedIndex = index;
    this.currentPageNumber = pageNumber;

    this.totalPages = this.pages.length;

    this.displayPages = this.calculateDisplayPages(this.currentPageNumber, this.totalPages);
    if (this.displayPages) {
      this.showRightEllipsis = !this.displayPages.includes(this.totalPages);
      this.showLeftEllipsis =
        this.currentPageNumber !== 0 && ![1, 2].some((val) => this.displayPages.includes(val));
    } else {
      this.showRightEllipsis = false;
      this.showLeftEllipsis = false;
    }
  }

  lastNumberClick(totalPages: number): void {
    const index = totalPages % 4;
    this.generatePagination(totalPages, index);
    this.updatePage(totalPages);
  }

  decreasePageNumber(): void {
    // reset the selected index if arrow click is past the displayed page numbers
    const index = this.selectedIndex <= 0 ? 3 : this.selectedIndex - 1;
    this.generatePagination(this.currentPageNumber - 1, index);
    this.updatePage(this.currentPageNumber);
  }

  increasePageNumber(): void {
    const index = this.selectedIndex >= 3 ? 0 : this.selectedIndex + 1;
    this.generatePagination(this.currentPageNumber + 1, index);
    this.updatePage(this.currentPageNumber);
  }

  /** @description update the page numbers shown in the displayed pagination */
  private calculateDisplayPages(currentPage: number, totalPages: number): number[] {
    // If there are less than 5 pages, then just display them all
    if (totalPages < 5) {
      return [...Array(totalPages).keys()].map((i) => i + 1);
    }

    // If we are on the 3rd page (special cases)
    if (currentPage === 3) {
      return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1];
    }

    // If we are on the 3rd page from the end (special case)
    if (currentPage === totalPages - 2) {
      return [currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
    }

    // If we are not on the end caps
    if (currentPage !== 1 && currentPage !== totalPages) {
      return [currentPage - 1, currentPage, currentPage + 1];
    }

    // If we are on the first page
    if (currentPage === 1) {
      return [currentPage, currentPage + 1, currentPage + 2];
    }

    // We must be on the last page
    return [currentPage - 2, currentPage - 1, currentPage];
  }

  trackByFn(index: number, _item: number): number {
    return index;
  }
}
